import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { IconChevronRight, IconFolder, IconFolderOpen } from './icons';
import { cn, focusRing } from './utils';

export interface TreeNode {
  readonly id: string;
  readonly label: string;
  /** Contador exibido à direita (ex.: documentos no tipo). */
  readonly count?: number;
  readonly disabled?: boolean;
  readonly children?: readonly TreeNode[];
  /** Payload livre para quem consome (ex.: o DocumentTypeNode inteiro). */
  readonly meta?: unknown;
}

export interface TreeViewProps {
  readonly nodes: readonly TreeNode[];
  readonly selectedId: string | null;
  readonly onSelect: (node: TreeNode | null) => void;
  readonly ariaLabel: string;
  /** Ids expandidos. Não controlado quando ausente. */
  readonly expandedIds?: readonly string[];
  readonly onExpandedChange?: (ids: string[]) => void;
  readonly className?: string;
  /** Nó sintético no topo (ex.: "Lista Mestra" = todos os tipos). */
  readonly rootNode?: { readonly label: string; readonly count?: number };
  readonly renderNodeSuffix?: (node: TreeNode) => ReactNode;
}

interface FlatNode {
  readonly node: TreeNode;
  readonly level: number;
  readonly hasChildren: boolean;
  readonly expanded: boolean;
}

/**
 * TreeView — hierarquia de tipos de documento.
 *
 * ACESSIBILIDADE — padrão APG "Tree View", teclado completo:
 *   ↓ / ↑        move entre nós VISÍVEIS (não entra em ramo fechado)
 *   →            abre o nó; se já aberto, vai para o primeiro filho
 *   ←            fecha o nó; se já fechado, vai para o pai
 *   Home / End   primeiro / último nó visível
 *   Enter/Espaço seleciona
 *   a–z          salta para o próximo nó cujo rótulo começa pela letra
 *
 * O contêiner usa `tabindex` roving: só UM nó é tabulável por vez, então Tab
 * entra e sai da árvore inteira num toque em vez de percorrer 200 tipos.
 *
 * A hierarquia é por ID (`parentId`), nunca por nome — o legado casava as duas
 * árvores pelo NOME do tipo (ged.md §10.2, achado C14), o que quebrava com
 * tipos homônimos em ramos diferentes.
 */
export function TreeView({
  nodes,
  selectedId,
  onSelect,
  ariaLabel,
  expandedIds,
  onExpandedChange,
  className,
  rootNode,
  renderNodeSuffix,
}: TreeViewProps) {
  const [internalExpanded, setInternalExpanded] = useState<string[]>(() =>
    // Abre o primeiro nível por padrão: uma árvore toda fechada esconde a
    // estrutura e obriga a explorar às cegas.
    nodes.filter((n) => (n.children?.length ?? 0) > 0).map((n) => n.id),
  );
  const expanded = expandedIds ?? internalExpanded;
  const setExpanded = onExpandedChange ?? setInternalExpanded;

  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedId, setFocusedId] = useState<string | null>(rootNode ? '__root__' : (nodes[0]?.id ?? null));

  const flat = useMemo(() => flatten(nodes, expanded, rootNode ? 1 : 0), [nodes, expanded, rootNode]);

  const visibleIds = useMemo(
    () => (rootNode ? ['__root__', ...flat.map((f) => f.node.id)] : flat.map((f) => f.node.id)),
    [flat, rootNode],
  );

  const focusNode = useCallback((id: string) => {
    setFocusedId(id);
    const element = containerRef.current?.querySelector<HTMLElement>(`[data-node-id="${cssEscape(id)}"]`);
    element?.focus();
  }, []);

  const toggle = useCallback(
    (id: string, open: boolean) => {
      setExpanded(open ? [...new Set([...expanded, id])] : expanded.filter((e) => e !== id));
    },
    [expanded, setExpanded],
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, id: string) => {
    const index = visibleIds.indexOf(id);
    const entry = flat.find((f) => f.node.id === id);

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        const next = visibleIds[index + 1];
        if (next) focusNode(next);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const previous = visibleIds[index - 1];
        if (previous) focusNode(previous);
        break;
      }
      case 'ArrowRight': {
        event.preventDefault();
        if (!entry) break;
        if (entry.hasChildren && !entry.expanded) {
          toggle(id, true);
        } else if (entry.hasChildren) {
          const firstChild = entry.node.children?.[0];
          if (firstChild) focusNode(firstChild.id);
        }
        break;
      }
      case 'ArrowLeft': {
        event.preventDefault();
        if (!entry) break;
        if (entry.hasChildren && entry.expanded) {
          toggle(id, false);
        } else {
          const parent = findParent(nodes, id);
          if (parent) focusNode(parent.id);
          else if (rootNode) focusNode('__root__');
        }
        break;
      }
      case 'Home': {
        event.preventDefault();
        const first = visibleIds[0];
        if (first) focusNode(first);
        break;
      }
      case 'End': {
        event.preventDefault();
        const last = visibleIds[visibleIds.length - 1];
        if (last) focusNode(last);
        break;
      }
      case 'Enter':
      case ' ': {
        event.preventDefault();
        if (id === '__root__') onSelect(null);
        else if (entry && !entry.node.disabled) onSelect(entry.node);
        break;
      }
      default: {
        // Busca por primeira letra (padrão APG).
        if (event.key.length === 1 && /\p{L}/u.test(event.key)) {
          const letter = event.key.toLowerCase();
          const ordered = [...visibleIds.slice(index + 1), ...visibleIds.slice(0, index + 1)];
          const match = ordered.find((candidateId) => {
            if (candidateId === '__root__') return rootNode?.label.toLowerCase().startsWith(letter);
            const candidate = flat.find((f) => f.node.id === candidateId);
            return candidate?.node.label.toLowerCase().startsWith(letter);
          });
          if (match) {
            event.preventDefault();
            focusNode(match);
          }
        }
        break;
      }
    }
  };

  const activeTabbableId = focusedId ?? visibleIds[0] ?? null;

  return (
    <div
      ref={containerRef}
      role="tree"
      aria-label={ariaLabel}
      className={cn('min-w-0 select-none', className)}
    >
      {rootNode ? (
        <div
          role="treeitem"
          data-node-id="__root__"
          aria-level={1}
          aria-selected={selectedId === null}
          aria-expanded={nodes.length > 0 ? true : undefined}
          tabIndex={activeTabbableId === '__root__' ? 0 : -1}
          onFocus={() => setFocusedId('__root__')}
          onKeyDown={(event) => handleKeyDown(event, '__root__')}
          onClick={() => onSelect(null)}
          className={cn(
            'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium',
            selectedId === null
              ? 'bg-brand-subtle text-brand'
              : 'text-content hover:bg-surface-sunken',
            focusRing,
          )}
        >
          <IconFolderOpen size={15} className="shrink-0" />
          <span className="min-w-0 flex-1 truncate">{rootNode.label}</span>
          {typeof rootNode.count === 'number' ? (
            <span className="shrink-0 text-xs text-content-subtle qc-tabular">
              {rootNode.count}
            </span>
          ) : null}
        </div>
      ) : null}

      <div role={rootNode ? 'group' : undefined} className={rootNode ? 'ml-2' : undefined}>
        {flat.map((entry) => {
          const isSelected = entry.node.id === selectedId;
          const isTabbable = activeTabbableId === entry.node.id;

          return (
            <div
              key={entry.node.id}
              role="treeitem"
              data-node-id={entry.node.id}
              aria-level={entry.level}
              aria-selected={isSelected}
              aria-expanded={entry.hasChildren ? entry.expanded : undefined}
              aria-disabled={entry.node.disabled || undefined}
              tabIndex={isTabbable ? 0 : -1}
              onFocus={() => setFocusedId(entry.node.id)}
              onKeyDown={(event) => handleKeyDown(event, entry.node.id)}
              onClick={() => {
                if (!entry.node.disabled) onSelect(entry.node);
              }}
              style={{ paddingLeft: `${(entry.level - 1) * 0.875 + 0.5}rem` }}
              className={cn(
                'flex cursor-pointer items-center gap-1.5 rounded-md py-1.5 pr-2 text-sm',
                isSelected ? 'bg-brand-subtle font-medium text-brand' : 'text-content hover:bg-surface-sunken',
                entry.node.disabled && 'cursor-not-allowed opacity-55',
                focusRing,
              )}
            >
              {entry.hasChildren ? (
                <button
                  type="button"
                  tabIndex={-1}
                  aria-hidden="true"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggle(entry.node.id, !entry.expanded);
                  }}
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-content-subtle hover:bg-surface-sunken"
                >
                  <IconChevronRight
                    size={14}
                    className={cn('transition-transform', entry.expanded && 'rotate-90')}
                  />
                </button>
              ) : (
                <span className="h-5 w-5 shrink-0" aria-hidden="true" />
              )}

              {entry.expanded ? (
                <IconFolderOpen size={15} className="shrink-0 text-content-subtle" />
              ) : (
                <IconFolder size={15} className="shrink-0 text-content-subtle" />
              )}

              <span className="min-w-0 flex-1 truncate">{entry.node.label}</span>

              {renderNodeSuffix?.(entry.node)}

              {typeof entry.node.count === 'number' ? (
                <span className="shrink-0 text-xs text-content-subtle qc-tabular">
                  {entry.node.count}
                  <span className="qc-sr-only">
                    {' '}
                    {entry.node.count === 1 ? 'documento' : 'documentos'}
                  </span>
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function flatten(
  nodes: readonly TreeNode[],
  expanded: readonly string[],
  levelOffset: number,
): FlatNode[] {
  const result: FlatNode[] = [];

  const walk = (list: readonly TreeNode[], level: number) => {
    for (const node of list) {
      const hasChildren = (node.children?.length ?? 0) > 0;
      const isExpanded = hasChildren && expanded.includes(node.id);
      result.push({ node, level, hasChildren, expanded: isExpanded });
      if (isExpanded && node.children) walk(node.children, level + 1);
    }
  };

  walk(nodes, levelOffset + 1);
  return result;
}

function findParent(nodes: readonly TreeNode[], childId: string): TreeNode | null {
  for (const node of nodes) {
    if (node.children?.some((child) => child.id === childId)) return node;
    if (node.children) {
      const deeper = findParent(node.children, childId);
      if (deeper) return deeper;
    }
  }
  return null;
}

function cssEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}
